import { ApiClient } from '../lib/client';
import { ApiResponse, ApiListResponse, type ModelConstructor } from '../lib/response';
import { ApiRequest } from '../lib/request';
import { ApiException } from '../lib/exception';
import type { BaseModel } from '../models/base';
// @ts-ignore
import asyncPool from 'tiny-async-pool';

export abstract class BaseService<T extends BaseModel> {
  model!: ModelConstructor<T>;

  apiUrl?: string;
  apiToken?: string;

  modelName = '';
  modelLabel = '';
  modelApiVersion = 1;

  /**
   * Default page size used by `listAll()`. Override per-service when rows are
   * small enough that a larger page cuts request count without bloating
   * response payloads (e.g. CustomerService uses 500).
   */
  listAllPageSize = 100;

  constructor({
    apiUrl,
    apiToken,
  }: {
    apiUrl?: string;
    apiToken?: string;
  } = {}) {
    this.apiUrl = apiUrl;
    this.apiToken = apiToken
  }

  get modelBaseUrl(): string {
    if (this.apiUrl) {
      return `${this.apiUrl}/v${this.modelApiVersion}/${this.modelName}`;
    }

    return `/api/v${this.modelApiVersion}/${this.modelName}`;
  }

  async request(request: ApiRequest): Promise<ApiResponse<any>> {
    const response = await ApiClient.request(request);

    return response;
  }

  async requestItem<T>(
    request: ApiRequest,
    model: ModelConstructor<T>
  ): Promise<ApiResponse<T>> {
    const response = await ApiClient.request(request);

    if (response.success) {
      return new ApiResponse<T>(response, model);
    }

    throw new ApiException(response);
  }

  async requestList<T>(
    request: ApiRequest,
    model: ModelConstructor<T>
  ): Promise<ApiListResponse<T>> {
    const response = await ApiClient.request(request);

    if (response.success) {
      return new ApiListResponse<T>(response, model);
    }

    throw new ApiException(response);
  }


  async get(
    id: string
  ): Promise<ApiResponse<T>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${id}`,
    });

    return await this.requestItem<T>(request, this.model);
  }

  async list({
    page = 1,
    limit = 100,
    search = '',
    filters = {},
    sort,
    modifiedSince,
    signal,
    accountId,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    filters?: Record<string, string>;
    sort?: {
      key: string,
      direction: 'asc' | 'desc',
    };
    modifiedSince?: Date;
    signal?: AbortSignal;
    accountId?: string;
  } = {}): Promise<ApiListResponse<T>> {
    const query: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };

    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        query[key] = value;
      }
    }

    if (search) {
      query.search = search;
    }

    if (sort) {
      query.sort = sort.key;
      query.sortDirection = sort.direction;
    }

    const headers: Record<string, string> = {};
    if (accountId) headers['X-Ovatu-Account'] = accountId;

    if (modifiedSince) {
      query.modifiedSince = (modifiedSince.getTime() / 1000).toString();
      headers['If-Modified-Since'] = modifiedSince.toUTCString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: this.modelBaseUrl,
      query,
      headers,
      signal,
    });

    return await this.requestList<T>(request, this.model)
  }

  /**
   * Hit the controller's `/count` route — every `LocationApiBaseController`
   * exposes one for free. Accepts the same flat `filters` shape as `list()`
   * (each key becomes a query param) and returns the integer total. Use this
   * any time you only need a count (e.g. polling for "still running?"
   * signals, badge counts) rather than fetching a full page of rows.
   */
  async count(
    { filters = {} }: { filters?: Record<string, string | number> } = {},
  ): Promise<ApiResponse<number>> {
    const query: Record<string, string> = {};
    for (const [key, value] of Object.entries(filters)) {
      query[key] = String(value);
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/count`,
      query,
    });

    return await this.request(request);
  }

  async delete(
    item: BaseModel,
  ): Promise<ApiResponse<T>> {
    const request = new ApiRequest({
      method: 'DELETE',
      url: `${this.modelBaseUrl}/${item.objectID()}`,
    });

    return await this.requestItem<T>(request, this.model)
  }

  async restore(
    item: BaseModel
  ): Promise<ApiResponse<T>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${item.objectID()}/restore`,
    });

    return await this.requestItem<T>(request, this.model)
  }

  async save(
    data: BaseModel
  ): Promise<ApiResponse<T>> {
    const isNewRecord = data.isNewRecord;

    const method = isNewRecord ? 'POST' : 'PUT';

    const url = isNewRecord
      ? this.modelBaseUrl
      : `${this.modelBaseUrl}/${data.objectID()}`;

    const request = new ApiRequest({
      method,
      url,
      data,
    });

    return await this.requestItem<T>(request, this.model)
  }

  async defaults(
    _item?: BaseModel
  ): Promise<ApiResponse<T>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/new`,
    });

    return await this.requestItem<T>(request, this.model)
  }

  async reorder(
    items: BaseModel[]
  ): Promise<ApiResponse<T>> {
    const data = [];

    for (const item of items) {
      const modelRequest: Record<string, any> = {
        id: item.objectID(),
      }

      data.push(modelRequest)
    }

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/reorder`,
      data,
    });

    return await this.requestItem<T>(request, this.model)
  }

  async ids(
    items: Array<BaseModel | string | number> = [],
    page: number = 1
  ): Promise<ApiListResponse<T>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/ids`,
      data: {
        ids: items.map((item) => {
          return typeof item === 'object' && item !== null ? item.objectID() : item
        }),
      },
      query: {
        page: page.toString(),
      },
    });

    return await this.requestList<T>(request, this.model)
  }

  async listAll(
    modifiedSince?: Date,
    update?: (page: number, pageCount: number, list: ApiListResponse<T>) => void,
    concurrency: number = 3,
    { signal, accountId }: { signal?: AbortSignal; accountId?: string } = {},
  ): Promise<T[]> {
    const items: T[] = [];
    const pageResponses: Record<number, ApiListResponse<T>> = {};

    let currentPage = 1;
    let totalPages = 0;

    const fetchPage = async (page: number): Promise<T[]> => {
      signal?.throwIfAborted();
      const response = await this.list({
        page,
        limit: this.listAllPageSize,
        modifiedSince,
        signal,
        accountId,
      });
      signal?.throwIfAborted();

      if (!response.success) {
        throw new Error(`Failed to fetch page ${page}`);
      }

      pageResponses[page] = response;

      if (response.paging?.pageCount) {
        totalPages = response.paging.pageCount;
      } else if (response.paging?.itemCount && response.paging?.limit) {
        totalPages = Math.ceil(response.paging.itemCount / response.paging.limit);
      }

      if (update) {
        update(Object.keys(pageResponses).length, totalPages || 1, response);
      }

      return response.items;
    };

    // Fetch the first page
    const firstPageItems = await fetchPage(currentPage);
    items.push(...firstPageItems);

    // If there's only one page or no paging info, return the items
    if (totalPages <= 1) {
      return items;
    }

    // Create array of remaining pages to fetch
    const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

    // Use async pool to fetch remaining pages concurrently
    const results: T[][] = [];
    const errors: Error[] = [];

    const iterator = async (page: number): Promise<T[]> => {
      try {
        return await fetchPage(page);
      } catch (error) {
        errors.push(error as Error);
        return [];
      }
    };

    const pool = asyncPool(concurrency, remainingPages, iterator);

    for await (const pageItems of pool) {
      if (pageItems && pageItems.length > 0) {
        results.push(pageItems);
      }
    }

    signal?.throwIfAborted();

    if (errors.length > 0) {
      console.error('Errors occurred while fetching pages:', errors);
      throw new Error('Unable to load all data');
    }

    // Flatten and combine all results
    const allRemainingItems = results.flat();
    items.push(...allRemainingItems);

    return items;
  }
}
